/**
 * What you could jump to from here.
 *
 * The rules are in `ship/jump.ts` and the galaxy index is `Navigator`; this is
 * the part that has to walk one to apply the other, which is why it lives with
 * the client rather than beside either — the same split `tutorialStart.ts` uses
 * and for the same reason.
 *
 * ── Why the cluster list is short ───────────────────────────────────────────
 *
 * There are seven hundred clusters and a gateway lookup is every system in one
 * against every system in the other. Doing that against all of them would be
 * four hundred thousand distance comparisons to answer a question whose honest
 * answer is almost always "none from here" — you are only ever the gateway for
 * a cluster or two, and only from one specific system.
 *
 * So candidate clusters are the nearest handful by centre, which bounds the
 * work and, more usefully, means every system in the galaxy offers a comparable
 * number of possibilities rather than the ones near the crowded core offering
 * hundreds.
 */

import {
  CLUSTER_JUMP_BASE,
  Navigator,
  getCluster,
  jumpVerdict,
  type ClusterId,
  type JumpVerdict,
  type SystemId,
  type SystemSummary,
} from '@interspacial/shared';

/*
 * ── Nothing is capped any more ──────────────────────────────────────────────
 *
 * There were two limits here and both were wrong for the same reason: they
 * bounded the *list* rather than the *reach*, so the sky and the route planner
 * described different galaxies.
 *
 * `MAX_LOCAL_TARGETS` kept the nearest dozen systems. A route's next hop is
 * chosen for direction, so it could sit thirteenth by distance — measured, and
 * it did — and never be drawn.
 *
 * `CLUSTERS_CONSIDERED` looked at the nearest ten clusters by centre. Measured
 * against the drive's actual crossing reach: a starter has up to eleven in
 * range and the best drive has forty, the furthest of them sitting at centre
 * rank forty. So the module was hiding most of what the planner would route
 * through.
 *
 * The module is a *toggle* precisely so it can afford to show everything while
 * it is up. Reach is now the only limit, and it comes from the same functions
 * the planner uses — `neighbours` and `warpNeighbours` — so the two cannot
 * disagree about what exists.
 */

export interface JumpTarget {
  systemId: SystemId;
  clusterId: ClusterId;
  name: string;
  /** Which cluster it belongs to, named, so a crossing says where it goes. */
  clusterName: string;
  /**
   * Unit direction from the system we are in.
   *
   * Computed here rather than by the renderer, because it is the same offset
   * `systemSky` uses to place the point of light this marker labels — and the
   * two agreeing is what puts the name *on* the star rather than near it.
   */
  direction: { x: number; y: number; z: number };
  /** Galactic units from where we are. */
  distance: number;
  sameCluster: boolean;
  verdict: JumpVerdict;
}

type Position = SystemSummary['galacticPosition'];

function gap(a: Position, b: Position): number {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}

/** Which way `to` lies from `from`. Straight up if they are the same point. */
function bearing(from: Position, to: Position): Position {
  const distance = gap(from, to);
  if (distance === 0) return { x: 0, y: 0, z: 1 };

  return {
    x: (to.x - from.x) / distance,
    y: (to.y - from.y) / distance,
    z: (to.z - from.z) / distance,
  };
}

export interface JumpTargetOptions {
  seed: string;
  from: SystemId;
  /** What this hull can cross in one hop — see `jumpRangeFor`. */
  range: number;
  /** Overdrive cores in the hold. */
  cores: number;
  /** What this hull can cross between clusters — see `clusterJumpRangeFor`. */
  clusterRange?: number;
  /**
   * A system that must appear whatever else is filtered out.
   *
   * The next hop on the planned route. Nothing in range is dropped any more, so
   * this only matters when the hop is *outside* range — a drive damaged since
   * the route was planned. Drawing it as out of reach explains why the console
   * is asking for something the module will not do; dropping it would not.
   */
  include?: SystemId | null;
  /**
   * How far out to list systems in your own cluster, as a multiple of range.
   *
   * More than one, deliberately: a target you cannot quite reach is the thing
   * that tells you a better drive is worth buying, and hiding it would leave
   * "out of reach" as a state the player never sees. Not much more than one,
   * because past that they are not near-misses, they are the rest of the
   * cluster.
   */
  reachMultiple?: number;
}

/**
 * Everywhere worth drawing a marker for.
 *
 * Local systems first and nearest-first within that, then the crossings — which
 * are rare, so they belong at the end where they read as the exception they
 * are.
 */
export function jumpTargets(options: JumpTargetOptions): JumpTarget[] {
  const { seed, from, range, cores, clusterRange, include = null, reachMultiple = 1.5 } = options;

  const navigator = new Navigator(seed);
  const here = navigator.get(from);
  if (!here) return [];

  const targets: JumpTarget[] = [];

  // ── Inside this cluster ──────────────────────────────────────────────────
  //
  // Everything the *route planner* would accept as a hop, which is what
  // `neighbours` answers, plus a band beyond it. The band is what keeps "out of
  // reach" a state the player ever sees, and so what tells them a better drive
  // is worth buying.
  const reachable = new Set(navigator.neighbours(from, range).map((system) => system.id));

  for (const candidate of navigator.systemsIn(here.clusterId)) {
    if (candidate.id === from) continue;

    const distance = gap(here.galacticPosition, candidate.galacticPosition);
    const beyond = !reachable.has(candidate.id) && distance > range * reachMultiple;

    // The routed hop is exempt: if the drive has since been damaged past
    // reaching it, saying so is far more use than silently dropping it.
    if (beyond && candidate.id !== include) continue;

    targets.push({
      systemId: candidate.id,
      clusterId: candidate.clusterId as ClusterId,
      name: candidate.name,
      clusterName: '',
      direction: bearing(here.galacticPosition, candidate.galacticPosition),
      distance,
      sameCluster: true,
      verdict: jumpVerdict({ sameCluster: true, distance, range, cores, atGateway: false }),
    });
  }

  targets.sort((a, b) => a.distance - b.distance);

  // ── Across to another ────────────────────────────────────────────────────
  //
  // The planner's own answer to "which clusters can I warp to", so the sky
  // cannot offer a crossing it would not route through, nor hide one it would.
  const crossing = clusterRange ?? CLUSTER_JUMP_BASE;

  for (const { id, gate } of navigator.warpNeighbours(here.clusterId, crossing)) {
    // Not our departure point. A crossing you cannot make from where you are
    // standing is a fact about the galaxy rather than a destination, and
    // drawing every one of them would bury the local systems.
    if (gate.from !== from) continue;

    const arrival = navigator.get(gate.to);
    if (!arrival) continue;

    targets.push({
      systemId: arrival.id,
      clusterId: id as ClusterId,
      name: arrival.name,
      clusterName: getCluster(seed, id as ClusterId).name,
      direction: bearing(here.galacticPosition, arrival.galacticPosition),
      distance: gate.distance,
      sameCluster: false,
      verdict: jumpVerdict({
        sameCluster: false,
        distance: gate.distance,
        range,
        clusterRange,
        cores,
        atGateway: true,
      }),
    });
  }

  return targets;
}
